/**
 * @fileoverview Login callback route for handling Okta authentication redirects.
 *
 * Processes the OAuth callback after user authentication, handles redirect completion,
 * manages authentication errors, and provides user feedback during the login process.
 */

import { useOktaAuth } from '@okta/okta-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { extractAttemptedUsername } from '../../features/auth/services';
import type { AuthErrorContext } from '../../features/auth/types';
import { errorService } from '../../lib/errorService';

/**
 * TanStack Router configuration for the login callback route.
 * Defines the /login/callback path used by Okta for OAuth redirects.
 */
export const Route = createFileRoute('/login/callback')({
  component: LoginCallback,
});

/**
 * Login callback component for OAuth redirect handling.
 *
 * Processes Okta authentication redirects, handles authentication completion,
 * manages error scenarios with comprehensive error reporting, and provides
 * loading feedback to users during authentication processing.
 */
function LoginCallback() {
  const { oktaAuth, authState } = useOktaAuth();
  const navigate = useNavigate();
  const handleRedirectAttempted = useRef(false); // To prevent double execution in StrictMode

  useEffect(() => {
    if (!oktaAuth) {
      return; // OktaAuth not initialized yet
    }

    if (
      oktaAuth.isLoginRedirect() &&
      !handleRedirectAttempted.current // Prevent double execution in StrictMode
    ) {
      handleRedirectAttempted.current = true;

      oktaAuth
        .handleRedirect()
        .then(() => {
          // Success: authState will update, and Security provider's restoreOriginalUri will handle navigation.
          // No explicit navigation needed here as restoreOriginalUri takes precedence.
        })
        .catch((err) => {
          // Extract attempted username from URL or error context
          const attemptedUsername = extractAttemptedUsername(err, {
            url:
              typeof window !== 'undefined' ? window.location.href : undefined,
          });

          const authContext: AuthErrorContext = {
            operation: 'callback',
            attempted_username: attemptedUsername,
            auth_state: {
              isAuthenticated: authState?.isAuthenticated,
              isPending: Boolean(authState?.isPending),
              error: authState?.error ? String(authState.error) : undefined,
            },
            additional_context: {
              currentUrl:
                typeof window !== 'undefined'
                  ? window.location.href
                  : undefined,
              userAgent:
                typeof navigator !== 'undefined'
                  ? navigator.userAgent
                  : undefined,
            },
          };

          errorService.reportAuthError(err, authContext);
          toast.error(err.message || 'Failed to login');

          // Fallback navigation if handleRedirect fails catastrophically
          navigate({ to: '/', replace: true });
        });
    } else if (
      authState &&
      !authState.isAuthenticated &&
      !oktaAuth.isLoginRedirect()
    ) {
      // This case could mean the user landed on /login/callback without an active Okta flow
      // or the flow was interrupted. Redirecting to home might be a safe default.
      navigate({ to: '/', replace: true });
    }
    // If authState.isAuthenticated is true, Security provider handles redirection.
    // If oktaAuth.isLoginRedirect() is true but already attempted, we wait for authState to update.
  }, [oktaAuth, authState, navigate]);

  return (
    <div className='p-4' aria-live='polite' aria-atomic='true'>
      <p className='text-lg'>Processing login, please wait...</p>
      <p className='text-sm text-muted-foreground'>
        You will be redirected shortly.
      </p>
    </div>
  );
}
